// ============================================================================
// OBT F2-A — Hedef kitle seçici
// ============================================================================
// Seçilebilir kişiler backend'in alt-zincir ucundan gelir ve TARGETABLE_ROLES'e
// filtrelenir. Yetki kapsamı dışı kişi UI'de LİSTELENMEZ; ancak asıl koruma
// backend'dedir (yayın anında yeniden doğrulanır). Rol/ID istemcide üretilmez.
//
// Doğrudan Formen seçildiğinde üst amir görünürlüğü kullanıcıya AÇIKLANIR;
// görünürlük kaydı backend davranışıdır, frontend üretmez.
// ============================================================================

import { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Info, AlertTriangle } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState } from '../ObtStates';
import { roleLabel, OBT_ROLE_LABELS } from './campaignEnums';

const ROLE_ORDER = ['mudur_yardimcisi', 'bolge_muhendisi', 'formen'];

export default function TargetPicker({
  candidates,
  loading,
  error,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
  onSelectMany,
  onDeselectMany,
  onReload,
}) {
  const grouped = useMemo(() => {
    const groups = {};
    for (const c of candidates) {
      (groups[c.roleSlug] = groups[c.roleSlug] || []).push(c);
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
    }
    return groups;
  }, [candidates]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const formenSelected = useMemo(
    () =>
      candidates.some(
        (c) => c.roleSlug === 'formen' && selectedSet.has(c.personnelId)
      ),
    [candidates, selectedSet]
  );

  // Formen olmayan (BM/MY) seçili hedefler: OBT cevabı park sahibi Formen'den
  // alınır; üst amirler yalnız takip/inceleme için görür. Bu kişiler doğrudan
  // hedeflenirse çoğu zaman cevaplayabilecekleri asli park bulunmaz.
  const nonFormenSelectedNames = useMemo(
    () =>
      candidates
        .filter(
          (c) => c.roleSlug !== 'formen' && selectedSet.has(c.personnelId)
        )
        .map((c) => c.name)
        .filter(Boolean),
    [candidates, selectedSet]
  );

  if (loading) return <ObtLoading label="Hedef kişiler yükleniyor…" />;
  if (error) return <ObtErrorState error={error} onRetry={onReload} />;
  if (candidates.length === 0) {
    return (
      <ObtEmptyState
        title="Hedeflenebilecek kişi bulunamadı"
        description="Kendi hiyerarşik kapsamınızda bilgi talebi gönderilebilecek bir kişi görünmüyor."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {selectedIds.length} kişi seçildi
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onSelectAll}>
            Tümünü seç
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={selectedIds.length === 0}
          >
            Temizle
          </Button>
        </div>
      </div>

      {ROLE_ORDER.filter((role) => grouped[role]?.length).map((role) => {
        const groupIds = grouped[role].map((p) => p.personnelId);
        const selectedInGroup = groupIds.filter((id) => selectedSet.has(id)).length;
        const allSelected = selectedInGroup === groupIds.length;
        return (
        <div
          key={role}
          role="group"
          aria-label={OBT_ROLE_LABELS[role] || role}
          className="space-y-2"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {OBT_ROLE_LABELS[role] || role} ({selectedInGroup}/{grouped[role].length}{' '}
              seçili)
            </span>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onSelectMany && onSelectMany(groupIds)}
                disabled={allSelected || !onSelectMany}
              >
                Tümünü seç
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onDeselectMany && onDeselectMany(groupIds)}
                disabled={selectedInGroup === 0 || !onDeselectMany}
              >
                Temizle
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {grouped[role].map((person) => (
              <label
                key={person.personnelId}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50"
              >
                <Checkbox
                  checked={selectedSet.has(person.personnelId)}
                  onCheckedChange={() => onToggle(person.personnelId)}
                  aria-label={`${person.name} (${roleLabel(person.roleSlug)}) hedefle`}
                />
                <span className="truncate text-sm text-gray-800">
                  {person.name}
                </span>
              </label>
            ))}
          </div>
        </div>
        );
      })}

      {formenSelected && (
        <div
          role="note"
          className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800 ring-1 ring-inset ring-blue-200"
        >
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            Bu bilgi talebi doğrudan Formenlere gönderilecektir. İlgili Bölge
            Mühendisi ve üst amirler bilgi talebini takip edebilir.
          </span>
        </div>
      )}

      {nonFormenSelectedNames.length > 0 && (
        <div
          role="note"
          className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-300"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            <strong>Kademeli devir:</strong> Seçtiğiniz{' '}
            {nonFormenSelectedNames.length === 1
              ? nonFormenSelectedNames[0]
              : `${nonFormenSelectedNames.length} kişi`}{' '}
            bir <strong>üst amir</strong> (Bölge Mühendisi / Müdür Yardımcısı).
            Kendisi doğrudan cevaplamaz; bu bilgi talebini{' '}
            <strong>kendi panelindeki “Dağıtım Görevleri”nden</strong> ilgili
            Formenlerine dağıtır ve cevaplayan hedefler dağıtımdan sonra oluşur.
            (Bu yüzden yayınlanınca liste kartında “hedef” yerine{' '}
            <strong>“dağıtıcı”</strong> görünür.) Cevabı doğrudan almak isterseniz
            hedef olarak ilgili <strong>Formenleri</strong> de seçebilirsiniz.
          </span>
        </div>
      )}
    </div>
  );
}
